package fr.frux.feature.treedetail

import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import fr.frux.core.common.share.ShareOutcome
import fr.frux.core.designsystem.error.userMessage
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_add_photo
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_add_trait
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_favorite
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_moderate
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_observe_status
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_report
import fr.frux.feature.treedetail.resources.treedetail_login_prompt_report_photo
import fr.frux.feature.treedetail.resources.treedetail_photo_added
import fr.frux.feature.treedetail.resources.treedetail_photo_deleted
import fr.frux.feature.treedetail.resources.treedetail_photo_reported
import fr.frux.feature.treedetail.resources.treedetail_share_failed
import fr.frux.feature.treedetail.resources.treedetail_share_link_copied
import fr.frux.shared.presentation.TreeSighting
import fr.frux.shared.presentation.treeSightingLabel
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.stringResource

// Libellés des messages typés portés par TreeDetailState (#176). Fichier séparé de l'écran : le
// ViewModel n'émet que des types, et TreeDetailScreen est déjà au plafond de fonctions par fichier
// (detekt TooManyFunctions).

/**
 * Traduit le résultat du partage (#391) en snackbar, ou en silence quand il n'y a rien à dire
 * (partage effectif ou annulation volontaire) — extrait de `TreeDetailScreen` pour sa complexité
 * cyclomatique et le plafond de fonctions par fichier (detekt TooManyFunctions).
 *
 * Libellés chargés ici par `getString` et non passés depuis la composition : sur le web,
 * `stringResource` peut encore valoir `""` au premier partage (#438).
 */
internal suspend fun showShareOutcomeSnackbar(
    outcome: ShareOutcome,
    snackbarHostState: SnackbarHostState,
) {
    when (outcome) {
        ShareOutcome.COPIED -> snackbarHostState.showSnackbar(getString(Res.string.treedetail_share_link_copied))
        ShareOutcome.FAILED -> snackbarHostState.showSnackbar(getString(Res.string.treedetail_share_failed))
        ShareOutcome.SHARED, ShareOutcome.CANCELLED -> Unit
    }
}

/** Libellé de l'invite de connexion pour le geste refusé (#264). */
@Composable
internal fun TreeDetailLoginPrompt.message(): String =
    when (this) {
        TreeDetailLoginPrompt.ObserveStatus -> stringResource(Res.string.treedetail_login_prompt_observe_status)
        TreeDetailLoginPrompt.AddTrait -> stringResource(Res.string.treedetail_login_prompt_add_trait)
        TreeDetailLoginPrompt.Favorite -> stringResource(Res.string.treedetail_login_prompt_favorite)
        TreeDetailLoginPrompt.Report -> stringResource(Res.string.treedetail_login_prompt_report)
        TreeDetailLoginPrompt.AddPhoto -> stringResource(Res.string.treedetail_login_prompt_add_photo)
        TreeDetailLoginPrompt.ReportPhoto -> stringResource(Res.string.treedetail_login_prompt_report_photo)
        TreeDetailLoginPrompt.Moderate -> stringResource(Res.string.treedetail_login_prompt_moderate)
    }

/**
 * Libellé du passage humain (#420, ADR 0025) : les mots vivent dans `:shared`
 * ([treeSightingLabel]), partagés avec la vignette de liste (#427).
 *
 * [nowMillis] est l'horloge qui a servi à dériver [TreeSighting] côté ViewModel
 * (`TreeDetail.sightingNowMillis`), jamais une seconde lecture de `Clock.System.now()` ici : le
 * niveau affiché et la date qu'il porte doivent toujours venir de la même horloge, sous peine de
 * diverger (#420, ronde de revue).
 */
internal fun TreeSighting.label(
    lastObservedAtMillis: Long?,
    nowMillis: Long,
): String = treeSightingLabel(this, lastObservedAtMillis, nowMillis)

/** Libellé du retour d'une action sur la galerie (#223, #247). */
@Composable
internal fun TreeDetailPhotoMessage.message(): String =
    when (this) {
        TreeDetailPhotoMessage.Added -> stringResource(Res.string.treedetail_photo_added)
        TreeDetailPhotoMessage.Reported -> stringResource(Res.string.treedetail_photo_reported)
        TreeDetailPhotoMessage.Deleted -> stringResource(Res.string.treedetail_photo_deleted)
        is TreeDetailPhotoMessage.Failed -> error.userMessage()
    }
